<template>
  <div
    ref="elRef"
    class="TeaserPageCanvas"
  >
    <IsoCheckAnimation
      v-if="animationState === 'Check'"
      ref="checkAnimationRef"
      :parent="container"
    />

    <IsoTaxAnimation
      v-else-if="animationState === 'Tax'"
      ref="taxAnimationRef"
      :parent="container"
    />

    <IsoInvestmentAnimation
      v-else-if="animationState === 'Investing'"
      ref="investmentAnimationRef"
      :parent="container"
    />
  </div>
</template>

<script lang="ts" setup>
import useCamera from "./useCamera";
import useDisposer from "./useDisposer";
import { panGroupInjectionKey } from "./usePan";
import useSoftShadows from "./useSoftShadows";
import { cameraInjectionKey, canvasInjectionKey, sceneInjectionKey, viewportOffsetInjectionKey } from "./useThreeElement";
import useViewport from "./useViewport";
import delay from "./delay";
import throttle from "./throttle";
import { AmbientLight, Color, DirectionalLight, Group, OrthographicCamera, PCFSoftShadowMap, Scene, Vector3, WebGLRenderer } from "three";
import { nextTick, onBeforeUnmount, onMounted, provide, reactive, ref } from "vue";
import IsoCheckAnimation from "./IsoCheckAnimation.vue";
import IsoInvestmentAnimation from "./IsoInvestmentAnimation.vue";
import IsoTaxAnimation from "./IsoTaxAnimation.vue";
import Colors from "@cosine/lib/Colors";
import useGridTexture from "@cosine/components/onboarding/welcome/useGridTexture";
import IsoGround from "@cosine/components/onboarding/welcome/IsoGround";

const {
  viewport,
} = useViewport();

useSoftShadows();

const elRef = ref<HTMLElement>();
const checkAnimationRef = ref<InstanceType<typeof IsoCheckAnimation>>();
const taxAnimationRef = ref<InstanceType<typeof IsoTaxAnimation>>();
const investmentAnimationRef = ref<InstanceType<typeof IsoInvestmentAnimation>>();
const animationState = ref<"Check" | "Tax" | "Investing">("Check");
const {
  autoDispose,
} = useDisposer();

let renderer: WebGLRenderer;
let raf = 0;

const scene = new Scene();
scene.background = new Color(Colors.grey50);
const offsetGroup = new Group();
const container = new Group();
offsetGroup.add(container);
scene.add(offsetGroup);

const camera = new OrthographicCamera(-1, 1, 1, -1, 1, 1000);
camera.position.set(20, 20, 20);
const viewportOffset = ref(new Vector3());
camera.lookAt(scene.position);

const {
  cameraFactor,
} = useCamera();

const ambientLight = new AmbientLight(Colors.white, 0.5);
container.add(ambientLight);

const requestRender = throttle(() => {
  cancelAnimationFrame(raf);
  raf = requestAnimationFrame(render);
}, 1000 / 120);

const canvas = reactive({
  width: 1024,
  height: 768,
  requestRender,
});

Array(4).fill(0)
  .map((_, i, arr) => {
    const theta = i / arr.length * Math.PI;
    const intensity = 0.5 + i * 0.1;
    const r = 40;
    const y = 100;

    return addDirectionalLight(new Vector3(
      Math.cos(theta) * r,
      y,
      Math.sin(theta) * r,
    ), {
      intensity,
      castShadow: false,
    });
  });

function addDirectionalLight (vector: Vector3, {
  intensity,
  castShadow,
}: { intensity: number,
  castShadow: boolean, }): DirectionalLight {
  const light = new DirectionalLight(Colors.white, intensity);

  light.shadow.mapSize.width = 1024;
  light.shadow.mapSize.height = 1024;
  light.shadow.camera.near = 0.1;
  light.shadow.camera.far = 1000;

  const aspect = canvas.width / canvas.height;
  light.shadow.camera.left = -cameraFactor.value * aspect;
  light.shadow.camera.right = cameraFactor.value * aspect;
  light.shadow.camera.top = cameraFactor.value * aspect;
  light.shadow.camera.bottom = -cameraFactor.value * aspect;

  light.castShadow = castShadow;
  light.position.lerp(vector, 1);
  light.lookAt(scene.position);
  container.add(light);
  return light;
}

const {
  gridTexture,
} = useGridTexture("rgb(243, 243, 241)");

provide(sceneInjectionKey, scene);
provide(cameraInjectionKey, camera);
provide(canvasInjectionKey, canvas);
provide(viewportOffsetInjectionKey, viewportOffset);
provide(panGroupInjectionKey, container);

let resizeObserver: ResizeObserver | void;

onMounted(() => {
  renderer = autoDispose(new WebGLRenderer({
    antialias: true,
  }));
  renderer.setPixelRatio(window.devicePixelRatio);
  renderer.shadowMap.enabled = false;
  renderer.shadowMap.type = PCFSoftShadowMap;
  elRef.value?.prepend(renderer.domElement);

  autoDispose(new IsoGround(container, {
    texture: gridTexture.value,
  }));

  resizeObserver = new ResizeObserver(handleResize);
  if (elRef.value) { resizeObserver.observe(elRef.value); }
  handleResize();

  requestAnimationFrame(startAnimation);
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
});

async function startAnimation () {
  await delay(500);

  animationState.value = "Check";
  await nextTick();
  await checkAnimationRef.value?.animate();

  animationState.value = "Tax";
  await nextTick();
  await taxAnimationRef.value?.animateIn();

  animationState.value = "Investing";
  await nextTick();
  await investmentAnimationRef.value?.animateIn();

  container.position.set(0, 0, 0);
  await startAnimation();
}

function centerCameraForViewport () {
  const xOffset = -1;

  offsetGroup.position.set(
    xOffset,
    -0.5,
    1 - xOffset,
  );

  viewportOffset.value.lerp(offsetGroup.position, 1);
}

function render () {
  renderer.render(scene, camera);
}

function handleResize () {
  const {
    width,
    height,
  } = elRef.value?.getBoundingClientRect() || viewport.value;
  Object.assign(canvas, {
    width,
    height,
  });

  const minCanvasHeight = 400;
  const maxCanvasHeight = 800;
  const minCameraFactor = 4;
  const maxCameraFactor = 6;
  cameraFactor.value = Math.min(maxCameraFactor, minCameraFactor + (maxCameraFactor - minCameraFactor) * (canvas.height - minCanvasHeight) / (maxCanvasHeight - minCanvasHeight));

  const aspect = canvas.width / canvas.height;
  camera.left = -cameraFactor.value * aspect;
  camera.right = cameraFactor.value * aspect;
  camera.top = cameraFactor.value;
  camera.bottom = -cameraFactor.value;
  camera.updateProjectionMatrix();

  centerCameraForViewport();
  renderer.setSize(canvas.width, canvas.height);
  render();
}
</script>

<style lang="scss" scoped>
.TeaserPageCanvas {
  overflow: hidden;
}
</style>
